/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { format } from 'date-fns';
import { toET, etToISO } from '../utils/et';

function AddressFieldInline({ value, onChange, style }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (val) => {
    onChange(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/estimate/autocomplete?q=' + encodeURIComponent(val));
        setSuggestions(res.data || []);
        setShowDropdown((res.data || []).length > 0);
      } catch (e) { setSuggestions([]); }
    }, 250);
  };

  return (
    <div style={{ position: 'relative' }} ref={wrapperRef}>
      <input value={value} onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        style={style || { width: '100%' }} />
      {showDropdown && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
          border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => { onChange(s.label); setSuggestions([]); setShowDropdown(false); }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
              onMouseEnter={e => e.target.style.background = '#f5f5f3'}
              onMouseLeave={e => e.target.style.background = 'white'}>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EditTripModal({ trip, onClose, onSaved }) {
  const [form, setForm] = useState({
    scheduledTime: trip.scheduled_time ? format(toET(trip.scheduled_time), "yyyy-MM-dd'T'HH:mm") : '',
    askedTime: (trip.requested_time && trip.scheduled_time && Math.abs(new Date(trip.requested_time) - new Date(trip.scheduled_time)) > 60000)
      ? format(toET(trip.requested_time), "yyyy-MM-dd'T'HH:mm") : '',
    originAddress: trip.origin_address || '',
    destinationAddress: trip.destination_address || '',
    requiresWheelchair: trip.requires_wheelchair || false,
    patientType: trip.patient_type || 'wheelchair',
    notes: trip.notes || '',
    specialInstructions: trip.special_instructions || '',
    status: trip.status || 'pending_approval',
    isWillCall: !!trip.is_will_call,
    billToPatient: (trip.notes || '').toLowerCase().includes('bill to: patient'),
    patientMrn: trip.patient_mrn || '',
    prepayAmount: trip.prepay_amount != null ? String(trip.prepay_amount) : '',
    prepayHours: trip.prepay_hours != null ? String(trip.prepay_hours) : '',
    prepayStatus: trip.prepay_status || 'unpaid',
    prepayMethod: trip.prepay_method || '',
    openClock: trip.open_clock || false,
    // B-100: '' = auto (derive from pickup time), 'true'/'false' = forced override
    overnightOverride: trip.overnight_override === true ? 'true' : trip.overnight_override === false ? 'false' : '',
    overnightOverrideReason: trip.overnight_override_reason || '',
  });
  const [saving, setSaving] = useState(false);
  const [payEmail, setPayEmail] = useState('');
  const [sendingPayEmail, setSendingPayEmail] = useState(false);
  const [error, setError] = useState('');


  const handleSave = async () => {
    setSaving(true);
    try {
      // Add bill-to-patient marker to notes if toggled on
      let notes = form.notes || '';
      if (form.billToPatient && !notes.toLowerCase().includes('bill to: patient')) {
        notes = notes + (notes ? ' | ' : '') + 'Bill to: Patient';
      } else if (!form.billToPatient) {
        notes = notes.replace(/\s*\|\s*Bill to: Patient/i, '').replace(/^Bill to: Patient\s*\|?\s*/i, '');
      }
      // B-100: only send the overnight override when the admin changed it
      const priorOverride = trip.overnight_override === true ? 'true' : trip.overnight_override === false ? 'false' : '';
      const overridePayload = {};
      if (form.overnightOverride !== priorOverride) {
        overridePayload.overnightOverride = form.overnightOverride === '' ? null : form.overnightOverride === 'true';
        overridePayload.overnightOverrideReason = form.overnightOverrideReason || null;
      }
      // Save trip details
      const saveRes = await api.put(`/transports/${trip.id}`, {
        scheduledTime: etToISO(form.scheduledTime),
        requestedTime: form.askedTime ? etToISO(form.askedTime) : null,
        originAddress: form.originAddress || null,
        destinationAddress: form.destinationAddress || null,
        requiresWheelchair: form.requiresWheelchair,
        patientType: form.patientType,
        vehicleTypeUsed: form.requiresWheelchair ? 'wheelchair_van' : 'sedan',
        notes: notes || null,
        patientMrn: form.patientMrn || null,
        specialInstructions: form.specialInstructions || '',
        ...overridePayload,
        ...((trip.return_leg_of && !!form.isWillCall !== !!trip.is_will_call) ? { isWillCall: !!form.isWillCall } : {}),
      });
      // A quoted private-pay trip whose pickup just moved into (or out of) the
      // overnight window costs a different amount than the caller was told
      // (3985, Paula 2026-10-05). The server says so; make sure a human sees it.
      if (saveRes && saveRes.data && saveRes.data.requoteWarning) {
        window.alert(saveRes.data.requoteWarning);
      }
      // If status changed, use admin endpoint (no restrictions)
      if (form.status !== trip.status) {
        try {
          await api.put(`/transports/${trip.id}/admin-status`, { status: form.status });
        } catch (err) {
          // 409 = cancelling a leg of a quoted/prepaid booking: money warning first
          if (err.response?.status === 409 && err.response.data?.requiresPrepayAck) {
            if (window.confirm(err.response.data.warning)) {
              await api.put(`/transports/${trip.id}/admin-status`, { status: form.status, prepayAcknowledged: true });
            } else {
              setSaving(false);
              return;
            }
          } else {
            throw err;
          }
        }
      }
      // Prepayment (private pay only). Paid requires a method.
      // Only send fields the user actually CHANGED in this modal -- sending
      // unchanged (possibly stale/blank) values could wipe saved data if the
      // trip object that opened the modal was missing prepay columns.
      if (form.billToPatient || trip.prepay_amount != null) {
        if (form.prepayStatus === 'paid' && !form.prepayMethod) {
          setError('Select how the prepayment was received (cash, check, Zelle, or online)');
          setSaving(false);
          return;
        }
        const initAmount = trip.prepay_amount != null ? String(trip.prepay_amount) : '';
        const initHours = trip.prepay_hours != null ? String(trip.prepay_hours) : '';
        const prepayChanges = {};
        if (form.prepayAmount !== initAmount) prepayChanges.amount = form.prepayAmount === '' ? null : Number(form.prepayAmount);
        if (form.prepayHours !== initHours) prepayChanges.hours = form.prepayHours === '' ? null : Number(form.prepayHours);
        if (form.prepayStatus !== (trip.prepay_status || 'unpaid')) prepayChanges.status = form.prepayStatus;
        if (form.prepayMethod !== (trip.prepay_method || '')) prepayChanges.method = form.prepayMethod || null;
        if (form.openClock !== (trip.open_clock || false)) prepayChanges.openClock = form.openClock;
        if (Object.keys(prepayChanges).length > 0) {
          await api.put(`/transports/${trip.id}/prepay`, prepayChanges);
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      setError('Could not save — ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 440, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0db', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Edit trip — {trip.transport_number}</h2>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Status</label>
            <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} style={{ width: '100%' }}>
              <option value="pending_approval">Pending approval</option>
              <option value="approved">Approved</option>
              <option value="dispatched">Dispatched</option>
              <option value="en_route_pickup">En route to pickup</option>
              <option value="picked_up">Picked up</option>
              <option value="dropped_off">Dropped off</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Pickup time</label>
            <input type="datetime-local" value={form.scheduledTime} onChange={e => setForm(f => ({...f, scheduledTime: e.target.value}))} style={{ width: '100%' }} />
            {trip.return_leg_of && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, color: '#854F0B' }}>
                <input type="checkbox" checked={!!form.isWillCall} onChange={e => setForm(f => ({ ...f, isWillCall: e.target.checked }))} />
                Will-call return (patient calls when ready; the time above is only a placeholder)
              </label>
            )}
          </div>
          <div style={{ marginBottom: 12, background: '#FFF7E6', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 10px' }}>
            <label style={{ fontSize: 12, color: '#854F0B', display: 'block', marginBottom: 4 }}>Pushed? Time they first asked for <span style={{ color: '#999' }}>(optional)</span></label>
            <input type="datetime-local" value={form.askedTime} onChange={e => setForm(f => ({...f, askedTime: e.target.value}))} style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Overnight premium</label>
            <select value={form.overnightOverride} onChange={e => setForm(f => ({...f, overnightOverride: e.target.value}))} style={{ width: '100%' }}>
              <option value="">Auto (11pm-6:59am pickup = overnight)</option>
              <option value="true">Force overnight</option>
              <option value="false">Force NOT overnight</option>
            </select>
            {form.overnightOverride !== '' && (
              <input
                type="text"
                placeholder="Reason (required) — e.g. day-crew exception per ruling"
                value={form.overnightOverrideReason}
                onChange={e => setForm(f => ({...f, overnightOverrideReason: e.target.value}))}
                style={{ width: '100%', marginTop: 4 }}
              />
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Pickup address</label>
            <AddressFieldInline value={form.originAddress} onChange={v => setForm(f => ({...f, originAddress: v}))} style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Destination address</label>
            <AddressFieldInline value={form.destinationAddress} onChange={v => setForm(f => ({...f, destinationAddress: v}))} style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Patient type</label>
            <select value={form.patientType} onChange={e => setForm(f => ({...f, patientType: e.target.value}))} style={{ width: '100%' }}>
              <option value="wheelchair">Wheelchair</option>
              <option value="ambulatory">Ambulatory</option>
              <option value="bh">BH</option>
              <option value="201">BH 201</option>
              <option value="302">BH 302</option>
              <option value="303">BH 303</option>
              <option value="304">BH 304</option>
              <option value="305">BH 305</option>
              <option value="da">D&A</option>
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>MRN {!form.patientMrn && <span style={{ color: '#C62828', fontSize: 10 }}>(required for LGH billing)</span>}</label>
            <input value={form.patientMrn} onChange={e => setForm(f => ({...f, patientMrn: e.target.value}))} placeholder="e.g. 7036654" style={{ width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div onClick={() => setForm(f => ({...f, requiresWheelchair: !f.requiresWheelchair}))}
                style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.requiresWheelchair ? '#185FA5' : '#ddd', position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.requiresWheelchair ? 18 : 2, transition: 'left 0.15s' }} />
              </div>
              <span style={{ fontSize: 13 }}>Wheelchair van</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div onClick={() => setForm(f => ({...f, billToPatient: !f.billToPatient}))}
                style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.billToPatient ? '#BA7517' : '#ddd', position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.billToPatient ? 18 : 2, transition: 'left 0.15s' }} />
              </div>
              <span style={{ fontSize: 13 }}>Bill to patient</span>
            </div>
          </div>
          {(form.billToPatient || trip.prepay_amount != null) && (
            <div style={{ background: '#FDF8F0', border: '0.5px solid #E6C87A', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#854F0B', marginBottom: 8 }}>Private pay — prepayment</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 2 }}>Quoted price (this leg)</label>
                  <input type="number" step="0.01" value={form.prepayAmount} placeholder="e.g. 285.50"
                    onChange={e => setForm(f => ({...f, prepayAmount: e.target.value}))} style={{ width: '100%' }} />
                </div>
                <div style={{ width: 110 }}>
                  <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 2 }}>Hours included</label>
                  <input type="number" step="0.25" value={form.prepayHours} placeholder="e.g. 2"
                    onChange={e => setForm(f => ({...f, prepayHours: e.target.value}))} style={{ width: '100%' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select value={form.prepayStatus} onChange={e => setForm(f => ({...f, prepayStatus: e.target.value}))} style={{ flex: 1 }}>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
                <select value={form.prepayMethod} onChange={e => setForm(f => ({...f, prepayMethod: e.target.value}))} style={{ flex: 1 }}>
                  <option value="">Payment method...</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="zelle">Zelle</option>
                  <option value="card">Card (online)</option>
                  <option value="ach">Bank transfer (online)</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <div onClick={() => setForm(f => ({...f, openClock: !f.openClock}))}
                  style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.openClock ? '#854F0B' : '#ddd', position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.openClock ? 18 : 2, transition: 'left 0.15s' }} />
                </div>
                <span style={{ fontSize: 12 }}>Open clock — time beyond included hours billed at $68/hr</span>
              </div>
              <div style={{ fontSize: 10, color: '#999', marginBottom: 8 }}>Paid/unpaid, method, and open clock apply to both legs of a round trip.</div>
              {/* One-button payment email (Paula 2026-08-03): all figures come
                  from the saved trip — save quote changes first, then send. */}
              <div style={{ borderTop: '0.5px solid #E6C87A', paddingTop: 8 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 2 }}>Email the payment link (uses the SAVED quote — hit Save first if you changed it)</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="email" value={payEmail} placeholder="customer@example.com" autoComplete="off"
                    onChange={e => setPayEmail(e.target.value)} style={{ flex: 1 }} />
                  <button disabled={sendingPayEmail || !payEmail} onClick={async () => {
                    setSendingPayEmail(true);
                    try {
                      const res = await api.post(`/transports/${trip.id}/send-payment-email`, { email: payEmail.trim() });
                      alert(`Payment email sent to ${res.data.sentTo}\n${res.data.trips.join(' + ')} — total $${Number(res.data.total).toFixed(2)}${res.data.alreadyPaid ? ' (already paid — sent as a confirmation)' : ''}`);
                      setPayEmail('');
                    } catch (e) {
                      alert(e.response?.data?.error || 'Could not send the payment email');
                    } finally { setSendingPayEmail(false); }
                  }} style={{ fontSize: 12, padding: '6px 12px', background: '#854F0B', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', opacity: sendingPayEmail || !payEmail ? 0.5 : 1 }}>
                    {sendingPayEmail ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3} style={{ width: '100%', resize: 'none' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Special instructions <span style={{ fontSize: 10, color: '#854F0B' }}>(driver will be texted if set)</span></label>
            <textarea value={form.specialInstructions} onChange={e => setForm(f => ({...f, specialInstructions: e.target.value}))} rows={2} placeholder="Only add if the driver needs a specific alert about this trip" style={{ width: '100%', resize: 'none' }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              fontSize: 12, padding: '6px 14px', background: '#f0f0f0',
              border: '0.5px solid #e0e0db', borderRadius: 6, cursor: 'pointer',
            }}>
              Attach fax PDF or document
              <input type="file" accept=".pdf,image/*" onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  const formData = new FormData();
                  formData.append('file', f);
                  formData.append('category', 'transport');
                  formData.append('docType', 'fax');
                  formData.append('transportId', trip.id);
                  const up = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                  // Rosie reads name + MRN off the paperwork and fills what is missing (Paula 10/27)
                  const pw = up.data.paperwork || {};
                  const got = [pw.filled && pw.filled.name ? 'name' : null, pw.filled && pw.filled.mrn ? 'MRN ' + pw.filled.mrn : null].filter(Boolean);
                  alert('File attached' + (got.length ? '. Read from it: ' + got.join(', ') + ' (close and reopen to see it).' : '') + (pw.mismatch ? '\n\nCHECK: ' + pw.mismatch : ''));
                } catch (err) { alert('Upload failed'); }
              }} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e0e0db', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 16px' }}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

